export const COMPETITION_LEVELS = ["IPF", "EPF", "AEP-1", "AEP-2", "AEP-3", "WRPF"];

const DEFAULT_MALE_WEIGHT_CLASSES = [
  "-53 kg",
  "-59 kg",
  "-66 kg",
  "-74 kg",
  "-83 kg",
  "-93 kg",
  "-105 kg",
  "-120 kg",
  "+120  kg",
];

const DEFAULT_FEMALE_WEIGHT_CLASSES = [
  "-43 kg",
  "-47 kg",
  "-52 kg",
  "-57 kg",
  "-63 kg",
  "-69 kg",
  "-76 kg",
  "-84 kg",
  "+84 kg",
];

const WRPF_MALE_WEIGHT_CLASSES = [
  "-52 kg",
  "-56 kg",
  "-60 kg",
  "-67.5 kg",
  "-75 kg",
  "-82.5 kg",
  "-90 kg",
  "-100 kg",
  "-110 kg",
  "-125 kg",
  "-140 kg",
  "+140 kg",
];

const WRPF_FEMALE_WEIGHT_CLASSES = [
  "-44 kg",
  "-48 kg",
  "-52 kg",
  "-56 kg",
  "-60 kg",
  "-67.5 kg",
  "-75 kg",
  "-82.5 kg",
  "-90 kg",
  "+90 kg",
];

const PLUS_WEIGHT_CLASS_REGEX = /^\s*\+/;
const NUMBER_REGEX = /\d+(?:[.,]\d+)?/;

const normalizeLevel = (level) => String(level ?? "").trim().toUpperCase();

const parseWeightClass = (weightClass) => {
  const normalized = String(weightClass ?? "").trim();
  if (!normalized) {
    return { isPlus: false, value: null };
  }

  const isPlus = PLUS_WEIGHT_CLASS_REGEX.test(normalized);
  const numberMatch = normalized.match(NUMBER_REGEX);
  if (!numberMatch) {
    return { isPlus, value: null };
  }

  return {
    isPlus,
    value: Number.parseFloat(numberMatch[0].replace(",", ".")),
  };
};

export const isWrpfLevel = (level) => normalizeLevel(level) === "WRPF";

export const getClientGameDayCompetitionLevels = (currentLevel) => {
  if (isWrpfLevel(currentLevel)) {
    return ["WRPF"];
  }

  return COMPETITION_LEVELS.filter((level) => !isWrpfLevel(level));
};

export const hasFederationFamilyChanged = (fromLevel, toLevel) =>
  isWrpfLevel(fromLevel) !== isWrpfLevel(toLevel);

export const getWeightClasses = (level, gender) => {
  if (isWrpfLevel(level)) {
    return gender ? WRPF_FEMALE_WEIGHT_CLASSES : WRPF_MALE_WEIGHT_CLASSES;
  }

  return gender ? DEFAULT_FEMALE_WEIGHT_CLASSES : DEFAULT_MALE_WEIGHT_CLASSES;
};

const mapToTargetWeightClasses = (weightClass, targetWeightClasses) => {
  const currentWeightClass = String(weightClass ?? "").trim();
  if (!currentWeightClass) {
    return "";
  }

  const sourceParsed = parseWeightClass(currentWeightClass);

  if (sourceParsed.isPlus) {
    return (
      targetWeightClasses.find((target) => parseWeightClass(target).isPlus) ??
      currentWeightClass
    );
  }

  if (sourceParsed.value == null || Number.isNaN(sourceParsed.value)) {
    return currentWeightClass;
  }

  let closestWeightClass = currentWeightClass;
  let smallestDistance = Number.POSITIVE_INFINITY;
  let selectedValue = Number.NEGATIVE_INFINITY;

  targetWeightClasses.forEach((targetWeightClass) => {
    const targetParsed = parseWeightClass(targetWeightClass);
    if (targetParsed.isPlus || targetParsed.value == null) {
      return;
    }

    const distance = Math.abs(targetParsed.value - sourceParsed.value);
    const isCloser = distance < smallestDistance;
    const isTieWithHeavier =
      distance === smallestDistance && targetParsed.value > selectedValue;

    if (isCloser || isTieWithHeavier) {
      smallestDistance = distance;
      selectedValue = targetParsed.value;
      closestWeightClass = targetWeightClass;
    }
  });

  return closestWeightClass;
};

export const mapWeightClass = (weightClass, fromLevel, toLevel, gender) => {
  const currentWeightClass = String(weightClass ?? "").trim();
  if (!currentWeightClass) {
    return "";
  }

  if (!hasFederationFamilyChanged(fromLevel, toLevel)) {
    return currentWeightClass;
  }

  const targetWeightClasses = getWeightClasses(toLevel, gender);
  return mapToTargetWeightClasses(currentWeightClass, targetWeightClasses);
};

export const normalizeWeightClassForLevel = (weightClass, level, gender) => {
  const currentWeightClass = String(weightClass ?? "").trim();
  if (!currentWeightClass) {
    return "";
  }

  const targetWeightClasses = getWeightClasses(level, gender);
  if (targetWeightClasses.includes(currentWeightClass)) {
    return currentWeightClass;
  }

  return mapToTargetWeightClasses(currentWeightClass, targetWeightClasses);
};

export const remapSelectedClientsWeightClasses = (
  clients,
  fromLevel,
  toLevel
) => {
  if (!hasFederationFamilyChanged(fromLevel, toLevel)) {
    return 0;
  }

  let remappedClients = 0;

  clients?.forEach((client) => {
    if (!client?.selectedWeightClass) {
      return;
    }

    const mappedWeightClass = mapWeightClass(
      client.selectedWeightClass,
      fromLevel,
      toLevel,
      client.gender
    );

    if (mappedWeightClass !== client.selectedWeightClass) {
      if (typeof client.setProperty === "function") {
        client.setProperty("selectedWeightClass", mappedWeightClass);
      } else {
        client.selectedWeightClass = mappedWeightClass;
      }
      remappedClients += 1;
    }
  });

  return remappedClients;
};
